import React from 'react';

import {LoadingSpinner} from '@albert-io/atomic';
import {refreshSession} from '@albert-io/mandark/authentication';

import awaitMandarkQueries from 'lib/hocs/awaitMandarkQueries';
import type {UserModelV2} from 'resources/augmented/User/UserModel.v2';
import {getBaseUserQuery} from 'client/User/UserQueryUtil';
import NewStudentOnboarding from 'client/SignUp/NewSignUp/Onboarding/Student/NewStudentOnboarding';
import NewEducatorOnboarding from 'client/SignUp/NewSignUp/Onboarding/Educator/NewEducatorOnboarding';
import AccountTypeSelectionPrompt from 'client/SignUp/NewSignUp/Onboarding/AccountTypeSelectionPrompt';
import {SIGNUP_TASKS} from 'client/SignUp/NewSignUp/Onboarding/onboarding.types';
import sessionStore from 'client/Session/SessionStore';
import {invalidateInterest} from 'resources/mandark.resource';
import {redirect} from 'client/User/UserRedirectUtil';
import {history} from 'client/history';

function getUserQuery() {
  return getBaseUserQuery(sessionStore.getUserId()).include('standard_set_creators_v1');
}

// Module-level so the default keeps the single stable identity `defaultProps` gave it —
// an inline default param would hand the onboarding children a new function every render.
function defaultOnFinalStepComplete() {
  refreshSession().then(() => {
    const {pathname} = history.getCurrentLocation();
    global.scroll(0, 0);
    if (pathname === '/') {
      redirect();
    }
  });
}

interface Props {
  user: UserModelV2;
  onFinalStepComplete?: () => void;
  pendingSignupTasks?: string[];
  signupComplete?: boolean;
  pendingTasksLoading?: boolean;
  isTeacher?: boolean;
  children?: React.ReactNode;
}

/**
 * Routes the user through onboarding based on `pendingSignupTasks` from the GraphQL API,
 * rendering the appropriate step until signup is complete, then renders its children.
 */
function ForceOnboarding({
  user,
  onFinalStepComplete = defaultOnFinalStepComplete,
  pendingSignupTasks = [],
  signupComplete = false,
  pendingTasksLoading = false,
  isTeacher = false,
  children
}: Props) {
  const onComplete = () => {
    // invalidateInterest's param type over-requires filter/include/customQuery/customHeader;
    // they are optional at runtime. Same narrowing as Generic.model.tsx.
    invalidateInterest(getUserQuery().done() as Parameters<typeof invalidateInterest>[0]);
  };

  // New flow: use pendingSignupTasks from GraphQL to determine routing
  if (pendingTasksLoading) {
    return (
      <div className='flex h-screen items-center justify-center'>
        <LoadingSpinner size={3} />
      </div>
    );
  }

  // If signup is complete via the new system, render the app
  if (signupComplete) {
    return children;
  }

  const isAccountTypeSelectionPending = pendingSignupTasks.includes(
    SIGNUP_TASKS.ACCOUNT_TYPE_SELECTION
  );

  // if `account_type_selection` is pending (eg: user signed up with old flow and logged in with new flow on), need to choose account type.
  if (isAccountTypeSelectionPending) {
    return <AccountTypeSelectionPrompt />;
  }

  if (pendingSignupTasks.length > 0) {
    const onboardingProps = {
      user,
      onComplete,
      onFinalStepComplete
    };

    return isTeacher ? (
      <NewEducatorOnboarding {...onboardingProps} />
    ) : (
      <NewStudentOnboarding {...onboardingProps} />
    );
  }

  return children;
}

export default awaitMandarkQueries(
  () => ({
    queries: {
      user: getUserQuery()
    },
    passthroughProps: {}
  }),
  ForceOnboarding
);
